import { useState } from 'react';

import { TCreateProjectInputSchema } from '@/console/features/projects/components/create-project/create-project.types';
import { useDefineAssetTags } from '@/shared/features/tags/hooks/use-define-asset-tags/use-define-asset-tags';
import { useMultipartUpload } from '@/shared/hooks/use-multipart-upload/use-multipart-upload';
import {
  useCompleteProjectMultipartUploadMutation,
  useCreateProjectMutation,
} from '@/shared/redux/api/assets/projects/projects-api';
import { AssetTypeEnum } from '@/shared/types/types.generated';

export const useCreateProject = () => {
  const [_createProject] = useCreateProjectMutation();
  const [completeUpload] = useCompleteProjectMultipartUploadMutation();
  const { uploadFile, calculatePartsCount } = useMultipartUpload();
  const defineAssetTags = useDefineAssetTags();
  const [uploadPercentage, setUploadPercentage] = useState<number>(0);

  const createProject = async (input: TCreateProjectInputSchema) => {
    const { file, tags, vaultId: _, groupId, ...rest } = input;
    const project = {
      ...rest,
      groupId: groupId ? groupId : undefined,
    };
    const response = await _createProject({
      project: {
        ...project,
        parts: calculatePartsCount(file.size),
        fileSize: file.size,
      },
    }).unwrap();

    const parts = await uploadFile(
      response.createProject.uploadUrls,
      file,
      ({ percentage }) => setUploadPercentage(percentage),
    );

    const { projectId, versionId, uploadId } = response.createProject;

    if (tags && tags.length > 0) {
      await defineAssetTags({
        tags,
        assetId: projectId,
        assetType: AssetTypeEnum.Project,
      });
    }

    await completeUpload({
      projectId,
      versionId,
      uploadId,
      completeUploadInput: {
        fileName: file.name,
        parts,
      },
    });

    setUploadPercentage(0);
    return response.createProject;
  };

  return {
    createProject,
    uploadPercentage,
  };
};
